import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import MediaLibraryDrawer from '../components/media-library-drawer/media-library-drawer.vue';
import { useDrawer } from '@bc/shared';
import { h, ref } from 'vue';
import { getMedia } from '../services/api';
import type { Media, MediaResponse, UploadProgress, FailedFile, MediaLibraryMode } from '../types';
import { captureBrandsException } from '@core/plugins/sentry/helper';
import { createMediaUploadService, type MediaUploadService } from '../services/media-upload-service';
import { ITEMS_PER_PAGE } from '../constants';
import { useMediaLibraryAnalytics } from './use-media-library-analytics';
import { VNode } from 'vue';

// UI and drawer state
const isLoading = ref(false);

// Media state
const items = ref<Media[]>([]);
const pagination = ref<MediaResponse['meta']['page']>();
const totalMediaCount = ref(0);

// Upload state
const uploadedMedia = ref<Media[]>([]);
const isUploading = ref(false);
const uploadProgress = ref<UploadProgress>({ current: 0, total: 0, uploaded: 0, failed: 0 });
const failedUploadMedia = ref<FailedFile[]>([]);

// Upload service handlers
let mediaUploadService: MediaUploadService | undefined;
let unsubscribeUploadStatus: (() => void) | undefined;
let unsubscribeProgress: (() => void) | undefined;

// Media fetching functions
const fetchMedia = async (pageToFetch = 1, query = '') => {
  try {
    isLoading.value = true;
    const {
      data,
      meta: { page },
    } = await getMedia({ page: pageToFetch, query });
    items.value = data;
    pagination.value = page;

    // Update total count from server data
    totalMediaCount.value = page.total || 0;
  } finally {
    isLoading.value = false;
  }
};

interface UseMediaLibraryOptions {
  mode?: MediaLibraryMode;
  eventHandlers?: { onItemClick?: (item: Media) => void };
  slots?: { header?: () => VNode };
}

export const useMediaLibrary = (options?: UseMediaLibraryOptions) => {
  const { mode, eventHandlers, slots } = options || {};

  const drawer = useDrawer();
  const { isBelowLg } = useBreakpoints();
  const { trackImageImport } = useMediaLibraryAnalytics();

  const handlePageChange = (page: number, query: string) => {
    fetchMedia(page, query);
  };

  const handleSearch = (query: string) => {
    fetchMedia(1, query);
  };

  const handleNewMediaUploaded = (media: Media) => {
    uploadedMedia.value = [media, ...uploadedMedia.value];

    // Add the new media to the beginning of the items array (used for display in the grid)
    items.value = [media, ...items.value];

    // Increment total media count when new media is uploaded
    totalMediaCount.value++;

    // Ensure we don't exceed the items per page limit
    if (items.value.length > ITEMS_PER_PAGE) {
      items.value = items.value.slice(0, ITEMS_PER_PAGE);
    }
  };

  // Upload service management
  const initializeUploadService = () => {
    if (mediaUploadService) {
      return;
    }

    mediaUploadService = createMediaUploadService(
      handleNewMediaUploaded,
      (error, file) => {
        failedUploadMedia.value.push({ error, file });
      }
    );

    unsubscribeUploadStatus = mediaUploadService.onUploadStatusChange(async (status: boolean) => {
      isUploading.value = status;

      // When upload finishes (status changes from true to false), fetch media
      if (!status) {
        if (uploadProgress.value.uploaded > 0) {
          trackImageImport(uploadProgress.value.uploaded);
        }

        await fetchMedia();
      }
    });

    unsubscribeProgress = mediaUploadService.onUploadProgressChange((current: number, total: number, uploaded: number, failed: number) => {
      uploadProgress.value = { current, total, uploaded, failed };
    });
  };

  const cleanupUploadService = () => {
    if (!mediaUploadService) {
      return;
    }

    unsubscribeUploadStatus?.();
    unsubscribeProgress?.();

    uploadProgress.value = { current: 0, total: 0, uploaded: 0, failed: 0 };
    failedUploadMedia.value = [];
    mediaUploadService = undefined;
  };

  const processFiles = (files: FileList) => {
    if (isUploading.value) {
      return;
    }

    initializeUploadService();
    mediaUploadService?.processFiles(files);
  };

  const handleItemClick = (item: Media) => {
    eventHandlers?.onItemClick?.(item);
  };

  // Media library drawer
  const createMediaLibraryComponent = () => h(MediaLibraryDrawer, {
    mode,
    items: items.value,
    pagination: pagination.value,
    isLoading: isLoading.value,
    isUploading: isUploading.value,
    totalMediaCount: totalMediaCount.value,
    onPageChange: handlePageChange,
    onSearch: handleSearch,
    onMediaChange: processFiles,
    onItemClick: handleItemClick,
  }, {
    header: () => slots?.header?.()
  });

  const drawerOptions = {
    right: true,
    width: isBelowLg.value ? '100%' : '60%',
  };

  const openMediaLibraryDrawer = async () => {
    try {
      drawer.openDrawer(createMediaLibraryComponent, drawerOptions);
      await fetchMedia();
    } catch (e) {
      captureBrandsException(e);
    }
  };

  const closeMediaLibraryDrawer = () => {
    drawer.closeDrawer();
  };

  return {
    openMediaLibraryDrawer,
    closeMediaLibraryDrawer,
    cleanupUploadService,
    isUploading,
    uploadProgress,
    failedUploadMedia,
    totalMediaCount,
  };
};
